import React, { useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Grid, CircularProgress, Alert, Divider, Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { gsecEntriesApi, trialBalanceApi } from '../../services/api';

const colors = {
  tableHeaderBackground: '#f5f5f5',
  sectionBackground: '#eef3fb',
  lightBackground: '#f9f9f9',
  success: '#4caf50',
  error: '#f44336'
};

// Known chart-of-accounts code renames: the external ledger's historical
// entries stay tagged with whatever code was current when they were posted,
// so a renamed account shows its old and new codes as separate rows. Map old
// codes to their current canonical code before merging.
const ACCOUNT_CODE_ALIASES = {
  '111-101-170-044-44': '131-101-170-044-44'
};

const resolveAccountNet = (account) => {
  if (account?.net_balance !== undefined && account?.net_balance !== null && account?.net_balance !== '') {
    return Number(account.net_balance) || 0;
  }
  const opening =
    (Number(account?.opening_debit) || 0) - (Number(account?.opening_credit) || 0);
  const period =
    (Number(account?.total_debit) || 0) - (Number(account?.total_credit) || 0);
  return opening + period;
};

const mergeByAccountCode = (accounts) => {
  const merged = new Map();
  for (const raw of accounts || []) {
    const code = ACCOUNT_CODE_ALIASES[raw.account_code] || raw.account_code;
    const account = code === raw.account_code ? raw : { ...raw, account_code: code };
    const existing = merged.get(code);
    if (!existing) {
      merged.set(code, { ...account });
    } else {
      existing.total_debit = (Number(existing.total_debit) || 0) + (Number(account.total_debit) || 0);
      existing.total_credit = (Number(existing.total_credit) || 0) + (Number(account.total_credit) || 0);
      existing.net_balance = (Number(existing.net_balance) || 0) + (Number(account.net_balance) || 0);
      if (existing.opening_debit !== undefined || account.opening_debit !== undefined) {
        existing.opening_debit = (Number(existing.opening_debit) || 0) + (Number(account.opening_debit) || 0);
      }
      if (existing.opening_credit !== undefined || account.opening_credit !== undefined) {
        existing.opening_credit = (Number(existing.opening_credit) || 0) + (Number(account.opening_credit) || 0);
      }
      // Prefer a non-empty name; keep the latest non-empty spelling.
      if (account.account_name) existing.account_name = account.account_name;
      if (account.account_type) existing.account_type = account.account_type;
      if (account.account_category) existing.account_category = account.account_category;
    }
  }
  return Array.from(merged.values());
};

/**
 * Combined Trial Balance = Equity/main /trial-balance + GSec ledger
 * /gsec-entries/balance-sheet, same as Sherwood Equity "All Ledgers".
 */
const buildCombinedAccounts = (tbAccounts, gsecAccounts) => {
  const tagged = [
    ...(tbAccounts || []).map((a) => ({ ...a, _source: 'EQUITY' })),
    ...(gsecAccounts || []).map((a) => ({ ...a, _source: 'GSEC' }))
  ];

  const byCode = new Map();
  for (const raw of tagged) {
    const code = ACCOUNT_CODE_ALIASES[raw.account_code] || raw.account_code;
    const existing = byCode.get(code);
    const debit = Number(raw.total_debit) || 0;
    const credit = Number(raw.total_credit) || 0;
    const net = resolveAccountNet(raw);
    if (!existing) {
      byCode.set(code, {
        account_code: code,
        account_name: raw.account_name || '',
        account_type: raw.account_type || raw.account_category || 'Uncategorized',
        account_category: raw.account_category || raw.account_type || 'Uncategorized',
        total_debit: debit,
        total_credit: credit,
        net_balance: net,
        sources: [raw._source]
      });
    } else {
      existing.total_debit += debit;
      existing.total_credit += credit;
      existing.net_balance += net;
      if (raw.account_name) existing.account_name = raw.account_name;
      if (raw.account_type && existing.account_type === 'Uncategorized') {
        existing.account_type = raw.account_type;
      }
      if (raw.account_category && existing.account_category === 'Uncategorized') {
        existing.account_category = raw.account_category;
      }
      if (!existing.sources.includes(raw._source)) {
        existing.sources.push(raw._source);
      }
    }
  }
  return Array.from(byCode.values()).sort((a, b) =>
    String(a.account_code).localeCompare(String(b.account_code))
  );
};

const groupByType = (accounts) => {
  const groups = {};
  for (const account of accounts) {
    const key = account.account_type || account.account_category || 'Uncategorized';
    if (!groups[key]) groups[key] = [];
    groups[key].push(account);
  }
  return Object.entries(groups)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([type, rows]) => ({ type, accounts: rows }));
};

const TrialBalance = () => {
  const [asAtDate, setAsAtDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);

  const generateReport = async () => {
    if (!asAtDate) {
      setError('Please select an As at date');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const asAtYmd = format(asAtDate, 'yyyy-MM-dd');
      // Combined TB = Equity trial-balance + GSec entries balance sheet
      // (same composition as Sherwood Equity "All Ledgers", Earliest → as at).
      const [tbResult, gsecResult] = await Promise.all([
        trialBalanceApi.getTrialBalance({ endDate: asAtYmd }),
        gsecEntriesApi.getBalanceSheet({ endDate: asAtYmd }).catch((gsecErr) => {
          console.error('Error fetching GSec ledger for combined trial balance:', gsecErr);
          return null;
        })
      ]);

      if (!tbResult?.success) {
        setError(tbResult?.message || 'Failed to generate Trial Balance.');
        setReport(null);
        return;
      }

      const tbAccounts = mergeByAccountCode(tbResult.data?.accounts || []);
      const gsecAccounts = gsecResult?.success
        ? mergeByAccountCode(gsecResult.data?.accounts || [])
        : [];
      const combinedAccounts = buildCombinedAccounts(tbAccounts, gsecAccounts);
      const totalDebits = combinedAccounts.reduce((s, a) => s + (Number(a.total_debit) || 0), 0);
      const totalCredits = combinedAccounts.reduce((s, a) => s + (Number(a.total_credit) || 0), 0);
      const isBalanced = Math.abs(totalDebits - totalCredits) < 0.02;

      setReport({
        period: {
          endDate: asAtYmd,
          portfolio: 'All Ledgers (Equity + GSec)'
        },
        accounts: combinedAccounts,
        typeGroups: groupByType(combinedAccounts),
        totals: {
          total_debits: totalDebits,
          total_credits: totalCredits,
          is_balanced: isBalanced,
          account_count: combinedAccounts.length,
          generated_date: new Date().toISOString()
        }
      });
    } catch (err) {
      console.error('Error generating trial balance:', err);
      setError(
        err.response?.data?.message ||
        'Failed to generate Trial Balance. Please try again later.'
      );
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const n = Number(amount) || 0;
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  };

  // net = debit − credit → positive is DR, negative is CR (no minus sign).
  const formatNetAmount = (amount) => {
    const n = Number(amount) || 0;
    if (n === 0) return formatCurrency(0);
    const side = n > 0 ? 'DR' : 'CR';
    return `${formatCurrency(Math.abs(n))} ${side}`;
  };

  const asAtLabel =
    report?.period?.endDate || (asAtDate ? format(asAtDate, 'yyyy-MM-dd') : '');
  const typeGroups = report?.typeGroups || [];

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Combined Trial Balance
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }} elevation={2}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="As at"
                value={asAtDate}
                onChange={(newValue) => setAsAtDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={generateReport}
              disabled={loading || !asAtDate}
              fullWidth
            >
              Generate Report
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : report ? (
        <Paper sx={{ p: 3 }} elevation={3}>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Combined Trial Balance
            </Typography>
            <Typography variant="subtitle1">
              As at {asAtLabel}
              {report.period?.portfolio ? ` — ${report.period.portfolio}` : ''}
            </Typography>
          </Box>

          {typeGroups.length > 0 ? (
            typeGroups.map(({ type, accounts }) => {
              const subtotalDebit = accounts.reduce((s, a) => s + (Number(a.total_debit) || 0), 0);
              const subtotalCredit = accounts.reduce((s, a) => s + (Number(a.total_credit) || 0), 0);
              const subtotalNet = accounts.reduce((s, a) => s + resolveAccountNet(a), 0);
              return (
                <TableContainer key={type} component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: colors.sectionBackground }}>
                        <TableCell colSpan={6} sx={{ fontWeight: 'bold' }}>{type}</TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: colors.tableHeaderBackground }}>
                        <TableCell>Account Code</TableCell>
                        <TableCell>Account Name</TableCell>
                        <TableCell align="right">Debit</TableCell>
                        <TableCell align="right">Credit</TableCell>
                        <TableCell align="right">Net Amount</TableCell>
                        <TableCell>Sources</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {accounts.map((account) => (
                        <TableRow key={account.account_code} hover>
                          <TableCell>{account.account_code}</TableCell>
                          <TableCell>{account.account_name}</TableCell>
                          <TableCell align="right">
                            {account.total_debit ? formatCurrency(account.total_debit) : ''}
                          </TableCell>
                          <TableCell align="right">
                            {account.total_credit ? formatCurrency(account.total_credit) : ''}
                          </TableCell>
                          <TableCell align="right">
                            {formatNetAmount(resolveAccountNet(account))}
                          </TableCell>
                          <TableCell>
                            {(account.sources || []).join(', ')}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ backgroundColor: colors.lightBackground }}>
                        <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>Subtotal - {type}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(subtotalDebit)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(subtotalCredit)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatNetAmount(subtotalNet)}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            })
          ) : (
            <Typography align="center" sx={{ py: 4 }}>
              No account activity found as at this date
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            backgroundColor: report.totals?.is_balanced ? colors.success : colors.error,
            borderRadius: 1,
            color: 'white'
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {report.totals?.is_balanced ? 'Trial Balance is balanced' : 'Trial Balance is NOT balanced'}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Debit {formatCurrency(report.totals?.total_debits)} &nbsp;|&nbsp; Credit {formatCurrency(report.totals?.total_credits)}
            </Typography>
          </Box>

          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={`${report.totals?.account_count ?? 0} accounts`} />
            <Chip size="small" variant="outlined" label="Equity + GSec combined" />
            {report.totals?.generated_date && (
              <Chip
                size="small"
                variant="outlined"
                label={`Generated ${format(new Date(report.totals.generated_date), 'MMM d, yyyy HH:mm')}`}
              />
            )}
          </Box>
        </Paper>
      ) : null}
    </Box>
  );
};

export default TrialBalance;
